import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { AnnotationService, Annotation, onAnnotationsStateChanged } from '../../../services/annotation.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { LogService } from '../../../services/log.service';

/**
 * MTM-5990 - Annotation API service
 * Service for handling annotation-related API calls and utility functions
 * Uses MediaPlayerStoreService to access current state via instanceId
 */
@Injectable()
export class AnnotationApiService {
  private readonly annotationService: AnnotationService = inject(AnnotationService);
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly logService: LogService = inject(LogService);
  /**
   * Get annotations for a media item
   * Fetches both domain (private) and public annotations and merges them
   * Uses MediaPlayerStore to access current state via instanceId
   * @param instanceId - The instance ID to get the store for
   */
  public getAnnotations(instanceId: string): Observable<Annotation[]> {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) {
      return of([]);
    }

    const typeId: string | null = store.typeIdComputedSignal();
    const itemId: string | null = store.itemIdSignal();

    if (!typeId || !itemId) {
      return of([]);
    }

    const subject: Subject<Annotation[]> = new Subject<Annotation[]>();

    const apiParams: any = {
      projectId: store.projectIdSignal(),
      sectionId: store.sectionIdSignal(),
      subsectionId: store.subsectionIdSignal(),
      typeId,
      itemId,
      participants: store.projectParticipantsSignal(),
      privateConditionRequest: null,
    };

    forkJoin([
      this.annotationService.getMediaAnnotations({
        ...apiParams,
        privateConditionRequest: 'DOMAIN',
      }),
      this.annotationService.getMediaAnnotations({
        ...apiParams,
      }),
    ]).subscribe({
      next: ([privateAnnotations, publicAnnotations]: any) => {
        const data: any = [
          ...privateAnnotations.map((item: Annotation) => ({ ...item, isPublic: false })),
          ...publicAnnotations.map((item: Annotation) => ({ ...item, isPublic: true })),
        ];
        subject.next(data);
      },
      error: (err) => {
        subject.next([]);
        subject.complete();
      },
    });

    return subject.asObservable();
  }

  /**
   * Save an annotation entity (annotation, comment, etc.)
   * Uses MediaPlayerStore to access current state via instanceId
   * Emits annotation state changed event on success
   * @param entityInstanceId - The instance ID to get the store for
   */
  public saveAnnotation(entity: any, entityId: string, entityInstanceId: string, annotations: any[]): void {
    const isStoreExisted: boolean = this.mpStoreService.hasStore(entityInstanceId);
    if (!isStoreExisted) {
      return;
    }

    const store: MediaPlayerStore | null = this.mpStoreService.getStore(entityInstanceId);
    if (!store) {
      return;
    }

    const typeId: string | null = store.typeIdComputedSignal();
    const itemId: string | null = store.itemIdSignal();

    if (!itemId || itemId !== entityId) {
      return;
    }

    const annotationItem: any = store.annotationPluginSignal()?.item || store.annotationPluginSignal()?.options?.item;
    if (!this.isAnnotationItemMatchingState(store, itemId, annotationItem)) {
      return;
    }

    const isPagedItem: boolean = !!store.itemSignal()?.hasPage && !!store.activePageSignal();
    const entityType: string = isPagedItem ? 'PAGE' : 'ASSET';
    this.persistAnnotation(store, typeId, itemId, entity, entityType, annotations);
  }

  private isAnnotationItemMatchingState(store: MediaPlayerStore, itemId: string | null, annotationItem: any): boolean {
    if (!itemId || itemId !== annotationItem?.id) {
      return false;
    }

    const isPagedItem: boolean = !!store.itemSignal()?.hasPage && !!store.activePageSignal();
    if (!isPagedItem && store.itemSignal()?.projectId !== annotationItem?.projectId) {
      return false;
    }

    return true;
  }

  private persistAnnotation(
    store: MediaPlayerStore,
    typeId: string | null,
    itemId: string | null,
    entity: any,
    entityType: string,
    annotations: any[],
  ): void {
    this.annotationService
      .addNewAnnotation(
        store.projectIdSignal(),
        store.sectionIdSignal(),
        store.subsectionIdSignal(),
        typeId,
        itemId,
        entity,
        store.projectParticipantsSignal(),
        entityType
      )
      .subscribe({
        next: () => {
          this.emitAnnotationStateChanged(annotations, store);
          store.updateStore({ isBusy: false });
        },
        error: (err: any) => {
          this.logService.logMessage('error', 'Error saving annotation:', { err });
          store.updateStore({ isBusy: false });
        },
      });
  }

  /**
   * Emit annotation state changed event after saving
   * Uses MediaPlayerStore to access current state via instanceId
   * @param annotations - The updated annotations array
   * @param instanceId - The instance ID to get the store for
   */
  public emitAnnotationStateChanged(annotations: any[], store: MediaPlayerStore): void {
    const typeId: string | null = store.typeIdComputedSignal();
    const itemId: string | null = store.itemIdSignal();

    onAnnotationsStateChanged.emit({
      args: 'annotationStateChanged',
      data: annotations,
      projectId: store.projectIdSignal(),
      sectionId: store.sectionIdSignal(),
      subSectionId: store.subsectionIdSignal(),
      typeId,
      itemId,
      instanceId: store.instanceIdSignal(),
    });
  }

  /**
   * Check if annotations have comments before a specific date
   * Used to determine if old UI should be used
   * Uses MediaPlayerStore to access current annotations if not provided
   * @param instanceId - The instance ID to get the store for
   * @param annotations - Optional annotations array (defaults to state annotations)
   * @param targetDateString - Target date string (defaults to '2025-02-01')
   */
  public hasCommentsBeforeDate(
    instanceId: string,
    annotations?: any[],
    targetDateString: string = '2025-02-01'
  ): boolean {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    const annotationsToCheck: any[] = annotations || store?.annotationsSignal() || [];

    if (!annotationsToCheck || annotationsToCheck.length === 0) {
      return false;
    }

    const targetDate: number = (globalThis as any).moment
      ? (globalThis as any).moment(targetDateString).valueOf()
      : new Date(targetDateString).getTime();

    return annotationsToCheck.some((annotation: any) => {
      if (!annotation.comments || annotation.comments.length === 0) {
        return false;
      }

      return annotation.comments.some((comment: any) => {
        const dt: string | undefined = comment.meta?.datetime;
        return dt && new Date(dt).getTime() < targetDate;
      });
    });
  }
}
